import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Package, PlusCircle, Pencil, Eye, Copy, Pause, Send, Trash2, Play, Info, MessageSquareWarning, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  CATEGORIAS_DIGITALES,
  PRODUCTOS_EJEMPLO,
  MKT_GOLD,
  MKT_TINT,
} from "../../lib/marketplace";
import { apiGetProductos, apiAccionProducto, apiEliminarProducto } from "../../lib/marketplaceApi";
import { humanizeAxiosError } from "../../lib/api";
import { EstadoBadge, SELLER_NOTICE } from "./mktui";

const catLabel = (id) => CATEGORIAS_DIGITALES.find((c) => c.id === id)?.label || id;

function ProductoCard({ p, onAccion, ejemplo }) {
  const acciones = [
    { key: "editar", label: "Editar", Icon: Pencil },
    { key: "preview", label: "Vista previa", Icon: Eye },
    { key: "duplicar", label: "Duplicar", Icon: Copy },
    p.estado === "pausado"
      ? { key: "reanudar", label: "Reanudar", Icon: Play }
      : { key: "pausar", label: "Pausar", Icon: Pause },
    { key: "revision", label: "Enviar a revisión", Icon: Send },
    { key: "eliminar", label: "Eliminar", Icon: Trash2, danger: true },
  ];
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-soft overflow-hidden flex flex-col" data-testid={`producto-card-${p.id}`}>
      <div className="h-28 relative flex items-center justify-center" style={{ background: p.cover ? undefined : `linear-gradient(135deg, #FFFFFF, ${MKT_TINT})` }}>
        {p.cover ? <img src={p.cover} alt={p.titulo} className="w-full h-full object-cover" /> : <Package size={30} style={{ color: MKT_GOLD }} />}
        <span className="absolute top-2 left-2"><EstadoBadge estado={p.estado} testid={`producto-estado-${p.id}`} /></span>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <p className="text-[10px] font-bold uppercase tracking-wide" style={{ color: MKT_GOLD }}>{catLabel(p.categoria)}</p>
        <p className="mt-1 font-bold text-slate-900 text-sm leading-tight">{p.titulo}</p>
        <p className="mt-2 font-extrabold text-slate-900">S/ {Number(p.precio || 0).toFixed(2)}</p>
        <p className="mt-2 text-[11px] text-slate-400 inline-flex items-center gap-1"><Info size={11} /> {SELLER_NOTICE}</p>
        {p.comentarios?.length > 0 && (
          <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-2.5" data-testid={`producto-comentarios-${p.id}`}>
            <p className="text-[11px] font-bold text-amber-800 inline-flex items-center gap-1"><MessageSquareWarning size={12} /> Comentarios de revisión</p>
            <ul className="mt-1 space-y-1">
              {p.comentarios.map((cm, i) => (
                <li key={i} className="text-[11px] text-amber-700">• {cm.texto}</li>
              ))}
            </ul>
          </div>
        )}
        {!ejemplo && (
          <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-3 gap-1.5">
            {acciones.map((a) => (
              <button
                key={a.key}
                onClick={() => onAccion(a.key, p)}
                data-testid={`producto-accion-${a.key}-${p.id}`}
                title={a.label}
                className={`inline-flex flex-col items-center gap-0.5 py-2 rounded-lg text-[10px] font-semibold transition-colors ${a.danger ? "text-red-600 hover:bg-red-50" : "text-slate-500 hover:bg-slate-50 hover:text-slate-800"}`}
              >
                <a.Icon size={15} /> {a.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function MisProductos() {
  const navigate = useNavigate();
  const [productos, setProductos] = useState([]);
  const [cargando, setCargando] = useState(true);

  const cargar = async () => {
    try {
      setProductos(await apiGetProductos());
    } catch (e) {
      toast.error(humanizeAxiosError(e));
    } finally {
      setCargando(false);
    }
  };
  useEffect(() => { cargar(); }, []);

  const onAccion = async (accion, p) => {
    if (accion === "editar" || accion === "preview") return navigate(`/marketplace/productos/crear?id=${p.id}`);
    try {
      if (accion === "eliminar") {
        await apiEliminarProducto(p.id);
        toast.success("Producto eliminado.");
      } else {
        const endpoint = accion === "reanudar" ? "pausar" : accion === "revision" ? "enviar" : accion; // pausar/duplicar/enviar
        await apiAccionProducto(p.id, endpoint);
        toast.success(
          accion === "duplicar" ? "Producto duplicado (borrador)." :
          accion === "pausar" ? "Producto pausado." :
          accion === "reanudar" ? "Producto reanudado." : "Producto enviado a revisión.",
        );
      }
      await cargar();
    } catch (e) {
      toast.error(humanizeAxiosError(e));
    }
  };

  if (cargando) {
    return <div className="flex items-center justify-center py-24 text-slate-500" data-testid="productos-loading"><Loader2 className="animate-spin mr-2" /> Cargando…</div>;
  }

  return (
    <div className="space-y-6" data-testid="mis-productos">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-extrabold text-slate-900">Mis Productos</h2>
          <p className="text-sm text-slate-500">Administra tus productos digitales de terceros.</p>
        </div>
        <button onClick={() => navigate("/marketplace/productos/crear")} data-testid="crear-producto-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-bold shadow-sm hover:brightness-110 transition-all" style={{ backgroundColor: MKT_GOLD }}>
          <PlusCircle size={18} /> Crear producto
        </button>
      </div>

      {productos.length === 0 ? (
        <>
          <div className="rounded-2xl border border-slate-200 bg-white shadow-soft p-10 text-center" data-testid="productos-vacio">
            <span className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center" style={{ backgroundColor: MKT_TINT, color: MKT_GOLD }}>
              <Package size={26} />
            </span>
            <h3 className="mt-4 text-lg font-bold text-slate-900">Aún no tienes productos publicados.</h3>
            <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">Crea tu primer producto digital y envíalo a revisión para publicarlo en el Marketplace.</p>
            <button onClick={() => navigate("/marketplace/productos/crear")} data-testid="crear-producto-vacio-btn" className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-bold shadow-sm hover:brightness-110 transition-all" style={{ backgroundColor: MKT_GOLD }}>
              <PlusCircle size={16} /> Crear producto
            </button>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-sm font-bold uppercase tracking-widest text-slate-500">Así se verán tus productos</h3>
              <span className="text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">Preliminar</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="productos-ejemplo">
              {PRODUCTOS_EJEMPLO.map((p) => (
                <ProductoCard key={p.id} p={p} ejemplo onAccion={() => {}} />
              ))}
            </div>
          </div>
        </>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="productos-lista">
          {productos.map((p) => (
            <ProductoCard key={p.id} p={p} onAccion={onAccion} />
          ))}
        </div>
      )}
    </div>
  );
}
